import React, { useMemo, useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import { Virtuoso } from 'react-virtuoso';
import { lastActivitySortKey } from '../utils/lastActivitySortKey';
import { getTimelineEventList } from '../utils/timelineEvents';
import { parseLocalDateString } from '../utils/localDate';
import { usePersistedScroll, readStoredScroll } from '../utils/usePersistedScroll';
import {
  DESIGN_VIEWPORT_W,
  TIMELINE_APP_COL_MAX,
  TIMELINE_APP_COL_MIN,
  TIMELINE_APP_COL_PCT,
  TIMELINE_SIDE_COL_MAX,
  TIMELINE_SIDE_COL_MIN,
  TIMELINE_SIDE_COL_PCT,
  fluidPx,
} from '../layout/appChrome';
import GanttTimelineRow from './GanttTimelineRow';

/** Height of the sticky "Applications" + date header inside the scroll container. */
const TIMELINE_STICKY_HEADER_H = 40;

const isValidDate = (d) => d instanceof Date && !isNaN(d);

const clampPercent = (p) => Math.max(0, Math.min(100, Number.isFinite(p) ? p : 0));

const getAppCoords = (app) => {
  const preLat = app.geocode_lat;
  const preLng = app.geocode_lng;
  if (typeof preLat === 'number' && typeof preLng === 'number') {
    return { lat: preLat, lng: preLng };
  }
  return null;
};

// Helpers for month rounding
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);

const GanttTimeline = ({
  applications,
  dateRange: selectedDateRange,
  includeAllDates,
  onSelectApplication,
  onSelectProperty,
  selectedApplication,
  milestonesOnly = false,
  /** False while Timeline tab/pane is hidden — skip scroll until visible. */
  isActive = true,
}) => {
  // Canvas-based text measurement helper (accurate enough for label fit decisions)
  const measureTextPx = useMemo(() => {
    const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    const ctx = canvas ? canvas.getContext('2d') : null;
    const cache = new Map();
    return (text, font) => {
      if (!ctx || !text) return 0;
      const key = `${font}||${text}`;
      if (cache.has(key)) return cache.get(key);
      ctx.font = font;
      const w = ctx.measureText(text).width || 0;
      cache.set(key, w);
      return w;
    };
  }, []);

  const handleSelect = useCallback((app) => {
    if (onSelectApplication) onSelectApplication(app);
    const coords = getAppCoords(app);
    if (coords && onSelectProperty) {
      const address = app.address;
      onSelectProperty({ ...coords, zoom: 17, address });
    }
  }, [onSelectApplication, onSelectProperty]);

  // Refs for auto-scroll to selected row + persisted scroll.
  // Boot position is applied via Virtuoso initial* props (skip restore to avoid a second jump).
  const containerRef = usePersistedScroll('ls_timelineScroll', {
    restoreDeps: [applications.length],
    enabled: isActive,
    skipRestore: true,
  });
  const virtuosoRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const [scrollParentEl, setScrollParentEl] = useState(null);
  const lastScrolledAppIdRef = useRef(null);
  const wasActiveRef = useRef(false);
  /** Locked on first data paint so tab switches open in place (no scroll-from-top). */
  const bootLockedRef = useRef(false);
  const initialTopMostItemIndexRef = useRef(undefined);
  const initialScrollTopRef = useRef(undefined);

  const setScrollParent = useCallback((node) => {
    containerRef.current = node;
    setScrollParentEl((prev) => (prev === node ? prev : node));
  }, [containerRef]);

  const applicationsWithTimeline = useMemo(
    () => (applications || []).filter((app) => getTimelineEventList(app.timeline).length > 0),
    [applications]
  );

  // Calculate the overall date range across all applications, with rules:
  // - If includeAllDates is true: min/max from data, then round to month boundaries
  // - Else: use selectedDateRange.from/to as local calendar dates, then:
  //   minDate = start of month containing "from", maxDate = end of month containing "to"
  //   (from last day of M includes M; from first day of M starts at M; to first/last day of M includes M)
  const dateRange = useMemo(() => {
    if (applicationsWithTimeline.length === 0) return null;

    let minDate = null;
    let maxDate = null;

    if (!includeAllDates && selectedDateRange && selectedDateRange.from && selectedDateRange.to) {
      const fromD = parseLocalDateString(selectedDateRange.from);
      const toD = parseLocalDateString(selectedDateRange.to);
      if (fromD && toD) {
        minDate = startOfMonth(fromD);
        maxDate = endOfMonth(toD);
      } else {
        minDate = startOfMonth(new Date(selectedDateRange.from));
        maxDate = endOfMonth(new Date(selectedDateRange.to));
      }
    } else {
      // derive from data
      applicationsWithTimeline.forEach((app) => {
        getTimelineEventList(app.timeline).forEach((ev) => {
          const date = parseLocalDateString(ev.date);
          if (!date || !isValidDate(date)) return;
          if (!minDate || date < minDate) minDate = date;
          if (!maxDate || date > maxDate) maxDate = date;
        });
      });
      if (isValidDate(minDate) && isValidDate(maxDate)) {
        minDate = startOfMonth(minDate);
        maxDate = endOfMonth(maxDate);
      } else {
        return null;
      }
    }

    // NOTE: We intentionally do NOT pad the date range here.
    // Padding days can push the start into a partial prior month, which makes the first "month column" look squished.

    return { minDate, maxDate };
  }, [applicationsWithTimeline, includeAllDates, selectedDateRange]);

  // Calculate position on timeline
  const getPosition = useCallback((date) => {
    const dt =
      date instanceof Date && isValidDate(date)
        ? date
        : parseLocalDateString(date) ?? (date != null ? new Date(date) : null);
    if (!dateRange || !dt || !isValidDate(dt)) return 0;
    const { minDate, maxDate } = dateRange;
    const totalDays = (maxDate - minDate) / (1000 * 60 * 60 * 24);
    if (!isFinite(totalDays) || totalDays <= 0) return 0;
    const daysFromStart = (dt - minDate) / (1000 * 60 * 60 * 24);
    return (daysFromStart / totalDays) * 100;
  }, [dateRange]);

  // Generate month labels for the timeline header
  const monthLabels = useMemo(() => {
    if (!dateRange || !isValidDate(dateRange.minDate) || !isValidDate(dateRange.maxDate)) return [];
    const { minDate, maxDate } = dateRange;
    if (maxDate <= minDate) return [];
    const labels = [];
    const current = new Date(minDate.getFullYear(), minDate.getMonth(), 1);

    while (current <= maxDate) {
      labels.push({
        date: new Date(current),
        position: getPosition(current),
      });
      current.setMonth(current.getMonth() + 1);
    }

    return labels;
  }, [dateRange, getPosition]);

  const sortedApplications = useMemo(() => {
    return [...applicationsWithTimeline].sort((a, b) => {
      const d = lastActivitySortKey(b) - lastActivitySortKey(a);
      if (d !== 0) return d;
      return String(a.case_number || '').localeCompare(String(b.case_number || ''));
    });
  }, [applicationsWithTimeline]);

  const findAppIndex = useCallback((appId) => {
    if (appId == null) return -1;
    return sortedApplications.findIndex(
      (a) => a.id === appId || String(a.id) === String(appId)
    );
  }, [sortedApplications]);

  // Capture once per mount:
  // - selection changed while away → open on that row
  // - otherwise → restore last scroll offset (tab switch “cache”)
  if (!bootLockedRef.current && sortedApplications.length > 0) {
    bootLockedRef.current = true;
    let lastSel = null;
    try {
      lastSel = sessionStorage.getItem('ls_timelineLastSelId');
    } catch {
      /* ignore */
    }
    const selId = selectedApplication?.id ?? null;
    const selChangedWhileAway = selId != null && String(selId) !== String(lastSel ?? '');

    if (selChangedWhileAway) {
      const index = sortedApplications.findIndex(
        (a) => a.id === selId || String(a.id) === String(selId)
      );
      if (index >= 0) {
        initialTopMostItemIndexRef.current = {
          index,
          align: 'start',
          offset: -TIMELINE_STICKY_HEADER_H,
        };
        lastScrolledAppIdRef.current = selId;
      }
    } else {
      const top = readStoredScroll('ls_timelineScroll');
      if (top != null) initialScrollTopRef.current = top;
      if (selId != null) lastScrolledAppIdRef.current = selId;
    }
  }

  // Remember which case was selected while Timeline was open (for next mount boot).
  useEffect(() => {
    if (!isActive) return;
    try {
      sessionStorage.setItem(
        'ls_timelineLastSelId',
        selectedApplication?.id != null ? String(selectedApplication.id) : ''
      );
    } catch {
      /* ignore */
    }
  }, [isActive, selectedApplication?.id]);

  // Selection change while Timeline is already open → one smooth scroll.
  // Tab switch keeps the DOM mounted, so scrollTop is already correct — don't animate.
  // If selection changed while away, jump instantly (no smooth-from-top).
  useEffect(() => {
    if (!isActive) {
      wasActiveRef.current = false;
      return;
    }
    const becameActive = !wasActiveRef.current;
    wasActiveRef.current = true;

    if (!selectedApplication) return;
    const appId = selectedApplication.id;
    if (lastScrolledAppIdRef.current === appId) return;

    const index = findAppIndex(appId);
    if (index < 0) return;

    let cancelled = false;
    const behavior = becameActive ? 'auto' : 'smooth';
    const headerH = stickyHeaderRef.current?.offsetHeight || TIMELINE_STICKY_HEADER_H;
    const id = requestAnimationFrame(() => {
      if (cancelled) return;
      try {
        // Virtuoso adds `offset` to scrollTop. Item tops already sit below the in-flow
        // sticky header in the scroll parent; align:start puts them under the stuck bar.
        // Negative offset pulls scroll back so the row clears the header.
        virtuosoRef.current?.scrollToIndex({
          index,
          align: 'start',
          behavior,
          offset: -headerH,
        });
        lastScrolledAppIdRef.current = appId;
      } catch {
        // Virtuoso not ready — leave lastScrolled unset so a later effect can retry once
      }
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [selectedApplication, findAppIndex, isActive]);

  // --- Layout constants (fluid from Omen@150% / 1707 baseline; see appChrome.js) ---
  const [viewportW, setViewportW] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : DESIGN_VIEWPORT_W
  );
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isNarrow = viewportW > 0 && viewportW < 768;
  const APP_COL_W = fluidPx(
    viewportW,
    TIMELINE_APP_COL_PCT,
    isNarrow ? 132 : TIMELINE_APP_COL_MIN,
    isNarrow ? 172 : TIMELINE_APP_COL_MAX
  );
  // Keep a single coordinate system for grid + labels + bubbles.
  // We already pad the *date range* (padDays) so events/labels don't sit flush on edges.
  const TL_PAD_L = 0;
  const TL_PAD_R = 0;
  const HEADER_H = TIMELINE_STICKY_HEADER_H;
  const GRID_LINE = '#d9d9d9';      // minor grid
  const GRID_LINE_MAJOR = '#b7b7b7'; // major grid/dividers
  const GRID_LINE_MAJOR_W = 2;
  const SIDE_COL_W = fluidPx(
    viewportW,
    TIMELINE_SIDE_COL_PCT,
    isNarrow ? 48 : TIMELINE_SIDE_COL_MIN,
    isNarrow ? 64 : TIMELINE_SIDE_COL_MAX
  );
  const showSideCols = !includeAllDates && selectedDateRange && selectedDateRange.from && selectedDateRange.to;

  // --- Responsive month labels: "Jan" -> "J" -> hide months entirely ---
  const rangeHeaderRef = useRef(null);
  const [rangeHeaderWidth, setRangeHeaderWidth] = useState(0);
  const [monthLabelFont, setMonthLabelFont] = useState('500 12px Roboto, Arial, sans-serif');

  // Build year groups for the header (year on its own row, centered over the months it spans)
  const yearGroups = useMemo(() => {
    const groups = [];
    for (let i = 0; i < monthLabels.length; i++) {
      const label = monthLabels[i];
      if (!label || !isValidDate(label.date)) continue;
      const year = label.date.getFullYear();
      if (groups.length === 0 || groups[groups.length - 1].year !== year) {
        groups.push({ year, startIndex: i, endIndex: i });
      } else {
        groups[groups.length - 1].endIndex = i;
      }
    }
    return groups;
  }, [monthLabels]);

  useEffect(() => {
    const el = rangeHeaderRef.current;
    if (!el || !isActive) return;

    const update = () => {
      setRangeHeaderWidth(el.clientWidth || 0);
      // Match the absolute month label chip (0.75rem / 500), not the container's computed font.
      setMonthLabelFont('500 12px Roboto, Helvetica, Arial, sans-serif');
    };

    update();
    const t = window.setTimeout(update, 50);
    const t2 = window.setTimeout(update, 200);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }

    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, [showSideCols, monthLabels.length, isActive, APP_COL_W, SIDE_COL_W]);

  const monthLabelMode = useMemo(() => {
    if (!Array.isArray(monthLabels) || monthLabels.length === 0) {
      return { hideMonths: false, mode: 'short' };
    }

    // Prefer measured range width; fall back to sticky header timeline column.
    // Never use window.innerWidth — that ignores Filters/Cases panels.
    // Header and body share one scroll parent, so measured width matches the rows.
    let rangeWidthPx = rangeHeaderWidth;
    if (!rangeWidthPx && typeof document !== 'undefined') {
      const sticky = stickyHeaderRef.current;
      if (sticky) {
        const timelineCol = sticky.lastElementChild;
        if (timelineCol) {
          rangeWidthPx = Math.max(
            0,
            timelineCol.clientWidth - (showSideCols ? 2 * SIDE_COL_W : 0)
          );
        }
      }
    }
    if (!rangeWidthPx) {
      return { hideMonths: false, mode: 'short' };
    }

    const LABEL_INSET_PX = 6; // left padding from column line (matches translate)
    const SAFETY_PX = 2;

    const colPxAt = (i) => {
      const label = monthLabels[i];
      if (!label || !isValidDate(label.date)) return 0;
      const leftPct = clampPercent(getPosition(label.date));
      const rightPct =
        i < monthLabels.length - 1 && monthLabels[i + 1] && isValidDate(monthLabels[i + 1].date)
          ? clampPercent(getPosition(monthLabels[i + 1].date))
          : 100;
      return Math.max(0, ((rightPct - leftPct) / 100) * rangeWidthPx);
    };

    // One shared mode for every month: long → short → letter → hide.
    // If January needs to shrink, June/July shrink with it (no mixed labels).
    const rank = { long: 0, short: 1, letter: 2 };
    let mode = 'long';

    for (let i = 0; i < monthLabels.length; i++) {
      const label = monthLabels[i];
      if (!label || !isValidDate(label.date)) continue;
      const colPx = colPxAt(i);
      if (colPx <= 0) continue;

      const monthLong = label.date.toLocaleDateString('en-US', { month: 'long' });
      const monthShort = label.date.toLocaleDateString('en-US', { month: 'short' });
      const monthLetter = monthShort ? monthShort[0] : '';

      const neededLetter = measureTextPx(monthLetter, monthLabelFont) + LABEL_INSET_PX + SAFETY_PX;
      if (colPx < neededLetter) return { hideMonths: true, mode: 'letter' };

      const neededShort = measureTextPx(monthShort, monthLabelFont) + LABEL_INSET_PX + SAFETY_PX;
      const neededLong = measureTextPx(monthLong, monthLabelFont) + LABEL_INSET_PX + SAFETY_PX;

      let colMode = 'long';
      if (colPx < neededLong) colMode = 'short';
      if (colPx < neededShort) colMode = 'letter';
      if (rank[colMode] > rank[mode]) mode = colMode;
    }

    return { hideMonths: false, mode };
  }, [
    rangeHeaderWidth,
    monthLabels,
    monthLabelFont,
    measureTextPx,
    getPosition,
    showSideCols,
    SIDE_COL_W,
  ]);

  const selectedAppId = selectedApplication?.id;

  const itemContent = useCallback((index, app) => (
    <GanttTimelineRow
      app={app}
      isSelected={!!(selectedAppId && selectedAppId === app.id)}
      milestonesOnly={milestonesOnly}
      dateRange={dateRange}
      showSideCols={showSideCols}
      APP_COL_W={APP_COL_W}
      SIDE_COL_W={SIDE_COL_W}
      TL_PAD_L={TL_PAD_L}
      TL_PAD_R={TL_PAD_R}
      getPosition={getPosition}
      clampPercent={clampPercent}
      onSelect={handleSelect}
      stackStatus={isNarrow}
    />
  ), [
    selectedAppId,
    milestonesOnly,
    dateRange,
    showSideCols,
    APP_COL_W,
    SIDE_COL_W,
    getPosition,
    handleSelect,
    isNarrow,
  ]);

  const virtuosoComponents = useMemo(() => {
    const List = forwardRef(function GanttTimelineList({ style, children, ...props }, ref) {
      return (
        <Box
          ref={ref}
          {...props}
          style={{ ...style, position: 'relative' }}
        >
          {/* Unified vertical grid lines (drawn once, behind everything; height from Virtuoso list style) */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${APP_COL_W}px`,
              right: 0,
              pointerEvents: 'none',
              zIndex: 0,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${GRID_LINE_MAJOR_W}px`,
                backgroundColor: GRID_LINE_MAJOR,
              }}
            />
            {showSideCols && (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${SIDE_COL_W}px`,
                    top: 0,
                    bottom: 0,
                    width: `${GRID_LINE_MAJOR_W}px`,
                    backgroundColor: GRID_LINE_MAJOR,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    right: `${SIDE_COL_W}px`,
                    top: 0,
                    bottom: 0,
                    width: `${GRID_LINE_MAJOR_W}px`,
                    backgroundColor: GRID_LINE_MAJOR,
                  }}
                />
              </>
            )}
            <Box
              sx={{
                position: 'absolute',
                left: showSideCols ? `${SIDE_COL_W + TL_PAD_L}px` : TL_PAD_L,
                right: showSideCols ? `${SIDE_COL_W + TL_PAD_R}px` : TL_PAD_R,
                top: 0,
                bottom: 0,
              }}
            >
              {monthLabels.map((label, index) => {
                if (!label || !isValidDate(label.date)) return null;
                const leftPct = clampPercent(getPosition(label.date));
                const isYearBoundary = label.date.getMonth() === 0;
                return (
                  <Box
                    key={`grid-line-${index}`}
                    sx={{
                      position: 'absolute',
                      left: `${leftPct}%`,
                      top: 0,
                      bottom: 0,
                      width: isYearBoundary ? `${GRID_LINE_MAJOR_W}px` : '1px',
                      backgroundColor: isYearBoundary ? GRID_LINE_MAJOR : GRID_LINE,
                    }}
                  />
                );
              })}
            </Box>
          </Box>
          {children}
        </Box>
      );
    });

    return { List };
  }, [
    APP_COL_W,
    SIDE_COL_W,
    showSideCols,
    monthLabels,
    getPosition,
    GRID_LINE,
    GRID_LINE_MAJOR,
    GRID_LINE_MAJOR_W,
    TL_PAD_L,
    TL_PAD_R,
  ]);

  // Fallback UI if nothing to render
  if (!applications || applications.length === 0 || applicationsWithTimeline.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No timeline data available for the selected applications
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: '100%',
      width: '100%',
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      minWidth: 0,
    }}>
      {/*
        One scroll parent for sticky header + Virtuoso rows.
        Avoids scrollbar-width padding hacks that desync Past/Range/Future lines.
      */}
      <Box
        ref={setScrollParent}
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowY: 'auto',
          overflowX: 'clip',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <Box
          ref={stickyHeaderRef}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2000,
            display: 'grid',
            gridTemplateColumns: `${APP_COL_W}px 1fr`,
            height: `${HEADER_H}px`,
            backgroundColor: '#f5f5f5',
            borderBottom: '2px solid #e0e0e0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              minWidth: 0,
              bgcolor: '#f5f5f5',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
              Applications
            </Typography>
          </Box>
          <Box
            sx={{
              position: 'relative',
              minWidth: 0,
              display: 'grid',
              gridTemplateColumns: showSideCols ? `${SIDE_COL_W}px 1fr ${SIDE_COL_W}px` : '1fr',
              alignItems: 'stretch',
              bgcolor: '#f5f5f5',
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${GRID_LINE_MAJOR_W}px`,
                  backgroundColor: GRID_LINE_MAJOR,
                }}
              />
              {showSideCols && (
                <>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${SIDE_COL_W}px`,
                      top: 0,
                      bottom: 0,
                      width: `${GRID_LINE_MAJOR_W}px`,
                      backgroundColor: GRID_LINE_MAJOR,
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      right: `${SIDE_COL_W}px`,
                      top: 0,
                      bottom: 0,
                      width: `${GRID_LINE_MAJOR_W}px`,
                      backgroundColor: GRID_LINE_MAJOR,
                    }}
                  />
                </>
              )}
              <Box
                sx={{
                  position: 'absolute',
                  left: showSideCols ? `${SIDE_COL_W + TL_PAD_L}px` : TL_PAD_L,
                  right: showSideCols ? `${SIDE_COL_W + TL_PAD_R}px` : TL_PAD_R,
                  top: 0,
                  bottom: 0,
                }}
              >
                {monthLabels.map((label, index) => {
                  if (!label || !isValidDate(label.date)) return null;
                  const leftPct = clampPercent(getPosition(label.date));
                  const isYearBoundary = label.date.getMonth() === 0;
                  return (
                    <Box
                      key={`header-grid-line-${index}`}
                      sx={{
                        position: 'absolute',
                        left: `${leftPct}%`,
                        top: 0,
                        bottom: 0,
                        width: isYearBoundary ? `${GRID_LINE_MAJOR_W}px` : '1px',
                        backgroundColor: isYearBoundary ? GRID_LINE_MAJOR : GRID_LINE,
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            {showSideCols && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  zIndex: 2,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', px: 0.5 }}>
                  Past
                </Typography>
              </Box>
            )}

            <Box ref={rangeHeaderRef} sx={{ position: 'relative', minWidth: 0, zIndex: 2 }}>
              <Box sx={{ position: 'absolute', left: TL_PAD_L, right: TL_PAD_R, top: 2, height: 14 }}>
                {yearGroups.map((g) => {
                  const startLabel = monthLabels[g.startIndex];
                  const endLabel = monthLabels[g.endIndex];
                  if (!startLabel || !endLabel) return null;
                  const startPct = clampPercent(getPosition(startLabel.date));
                  return (
                    <Box
                      key={`year-${g.year}-${g.startIndex}`}
                      sx={{
                        position: 'absolute',
                        left: `${startPct}%`,
                        top: '50%',
                        transform: 'translate(6px, -50%)',
                        fontSize: '0.7rem',
                        color: '#777',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        lineHeight: 1,
                      }}
                    >
                      {g.year}
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ position: 'absolute', left: TL_PAD_L, right: TL_PAD_R, bottom: 2, height: 20 }}>
                {monthLabelMode.hideMonths ? null : monthLabels.map((label, index) => {
                  if (!label || !isValidDate(label.date)) return null;
                  const leftPct = clampPercent(getPosition(label.date));
                  const rightPct =
                    index < monthLabels.length - 1 && monthLabels[index + 1] && isValidDate(monthLabels[index + 1].date)
                      ? clampPercent(getPosition(monthLabels[index + 1].date))
                      : 100;
                  const colPct = Math.max(0, rightPct - leftPct);
                  const mode = monthLabelMode.mode || 'short';
                  const monthLong = label.date.toLocaleDateString('en-US', { month: 'long' });
                  const monthShort = label.date.toLocaleDateString('en-US', { month: 'short' });
                  const text =
                    mode === 'long' ? monthLong
                      : mode === 'letter' ? (monthShort ? monthShort[0] : '')
                        : monthShort;
                  return (
                    <Box
                      key={`month-${index}`}
                      sx={{
                        position: 'absolute',
                        left: `${leftPct}%`,
                        top: '50%',
                        transform: 'translate(6px, -50%)',
                        maxWidth: `calc(${colPct}% - 8px)`,
                        overflow: 'hidden',
                        textOverflow: 'clip',
                        fontSize: '0.75rem',
                        color: '#666',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        lineHeight: 1.2,
                      }}
                    >
                      {text}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {showSideCols && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  zIndex: 2,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', px: 0.5 }}>
                  Future
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {scrollParentEl ? (
          <Virtuoso
            ref={virtuosoRef}
            customScrollParent={scrollParentEl}
            style={{ width: '100%' }}
            data={sortedApplications}
            overscan={200}
            defaultItemHeight={50}
            computeItemKey={(index, app) => app.id ?? index}
            initialTopMostItemIndex={initialTopMostItemIndexRef.current}
            initialScrollTop={initialScrollTopRef.current}
            components={virtuosoComponents}
            itemContent={itemContent}
          />
        ) : null}
      </Box>

      {/* Legend — compact on phone so it doesn't steal the viewport */}
      <Box
        sx={{
          borderTop: '1px solid #e0e0e0',
          px: isNarrow ? 0.75 : 1,
          py: isNarrow ? 0.5 : 1,
          display: 'flex',
          gap: isNarrow ? 0.75 : 2,
          justifyContent: 'center',
          flexWrap: 'wrap',
          backgroundColor: '#fafafa',
          flexShrink: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {[
          { icon: PlayArrowIcon, label: isNarrow ? 'Filing' : 'Initial filing', iconFs: isNarrow ? 14 : 18 },
          { icon: ThumbUpAltOutlinedIcon, label: isNarrow ? 'Rec. Approve' : 'Recommends Approval', iconFs: isNarrow ? 12 : 16, stroke: 1 },
          { icon: ThumbDownAltOutlinedIcon, label: isNarrow ? 'Rec. Deny' : 'Recommends Denial', iconFs: isNarrow ? 12 : 16, stroke: 1 },
          { icon: CheckIcon, label: 'Approved', iconFs: isNarrow ? 14 : 18, stroke: 2 },
          { icon: CloseIcon, label: 'Denied', iconFs: isNarrow ? 14 : 18, stroke: 2 },
          { icon: UndoIcon, label: 'Withdrawn', iconFs: isNarrow ? 14 : 18, stroke: 2 },
        ].map(({ icon: Icon, label, iconFs, stroke }) => {
          const bubble = isNarrow ? 22 : 30;
          return (
            <Chip
              key={label}
              size="small"
              icon={(
                <Box
                  sx={{
                    width: bubble,
                    height: bubble,
                    borderRadius: '50%',
                    bgcolor: '#111',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: `${iconFs}px`,
                      color: 'white',
                      ...(stroke != null ? { '& path': { stroke: 'white', strokeWidth: stroke } } : null),
                    }}
                  />
                </Box>
              )}
              label={label}
              sx={{
                color: '#111',
                borderColor: '#e0e0e0',
                maxWidth: '100%',
                height: isNarrow ? 26 : undefined,
                fontSize: isNarrow ? '0.65rem' : undefined,
                '& .MuiChip-label': { px: isNarrow ? 0.75 : undefined },
                '& .MuiChip-icon': { ml: isNarrow ? '4px' : undefined },
              }}
              variant="outlined"
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default GanttTimeline;
